import { FC, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from 'components/Button';
import Error from 'components/form/Error';
import PasswordInput from 'components/form/Inputs/PasswordInput';
import publicAPI from 'utils/publicAxiosInstance';
import AuthenticationLayout from 'components/AuthLayout';
import PasswordRequirements from 'components/form/PasswordRequirements';
import isPasswordValid from 'utils/isPasswordValid';

const Form = styled.form`
  width: min(90%, 20rem);
  margin-top: 0.5rem;
  & > p {
    margin-top: 1.5rem;
  }
  a {
    display: flex;
    margin-top: 0.375rem;
    color: var(--primary-700);
    font-weight: 700;
    font-size: 0.9rem;
    &:hover {
      text-decoration: underline;
    }
  }

  button {
    margin-left: auto;
    margin-right: auto;
    margin-top: 2rem;
    padding: 0.7rem 1.1rem;
  }
`;

const ResetPassword: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [state, setState] = useState({
    password: '',
    passwordConfirm: '',
    error: '',
  });
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setState({ ...state, [e.target.name]: e.target.value, error: '' });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setLoading(true);
    if (state.password !== state.passwordConfirm) {
      setLoading(false);
      return setState({
        ...state,
        error: 'Les mots de passe ne correspondent pas.',
      });
    }

    if (!isPasswordValid(state.password)) {
      setLoading(false);
      return setState({
        ...state,
        error: "Le mot de passe n'est pas assez fort.",
      });
    }

    publicAPI
      .post('/auth/reset-password', {
        indent: searchParams.get('indent'),
        token: searchParams.get('token'),
        password: state.password,
      })
      .then(() => {
        setLoading(false);
        navigate('/authentication/password-changed');
      })
      .catch((err) => {
        setLoading(false);
        if (err.response && err.response.status === 400) {
          setState({
            ...state,
            error: 'Le lien de réinitialisation du mot de passe est invalide.',
          });
        } else {
          setState({ ...state, error: 'Une erreur est survenue.' });
        }
      });
  };

  return (
    <AuthenticationLayout
      title="Nouveau mot de passe"
      subtitle="Veuillez saisir le nouveau mot de passe de votre compte"
      backTo="/authentication/login"
      src_image="/logo_institut_principal.png"
    >
      {!(searchParams.get('indent') && searchParams.get('token')) ? (
        <Error style={{ marginTop: '2rem' }}>
          Ce lien de réinitialisation est invalide.
        </Error>
      ) : (
        <Form onSubmit={handleSubmit}>
          {state.error && <Error>{state.error}</Error>}
          <PasswordInput
            value={state.password}
            label="Mot de passe"
            onChange={handleChange}
            name="password"
            required
          />
          <PasswordInput
            value={state.passwordConfirm}
            label="Confirmation du mot de passe"
            onChange={handleChange}
            name="passwordConfirm"
            required
          />
          <PasswordRequirements password={state.password} marginTop={1} />
          <Button color="primary" loading={loading}>
            Valider
          </Button>
        </Form>
      )}
    </AuthenticationLayout>
  );
};

export default ResetPassword;
